<template>
  <Form :model="formData" ref="bomItemForm" :disabled="readonly">
    <FormItem :label="$t('bom_tool.manufacturer_part')" prop="manufacturer_part_id">
      <ManufacturerPartPicker
        :disabled="preventMalwareChange || (!isNew && formData.manufacturer_part_id)"
        v-model="formData.manufacturer_part_id"
        v-bind:manufacturerPart.sync="formData.primary_manufacturer_part">
      </ManufacturerPartPicker>
      <p style="font-size: 12px; color: #9ea7b4; text-align: right;">
        <router-link v-if="formData.manufacturer_part_id" :to="{ name: `menu.${itemPartType}.edit`, params: { partId: this.formData.manufacturer_part_id } }" target="_blank">
          {{ $t('bom_tool.reference_manufacturer_part_link_title') }}
        </router-link>
        <span v-else>{{ $t('bom_tool.manufacturer_part_select_help') }}</span>
      </p>
    </FormItem>
    <FormItem :label="$t('bom_tool.mfr_part_number')" prop="mpn">
      <Input v-model="formData.mpn" :disabled="preventMalwareChange"></Input>
    </FormItem>
    <FormItem :label="$t('bom_tool.mfr_name')" prop="brand">
      <SelectCompany v-model="formData.brand" :disabled="preventMalwareChange" transfer></SelectCompany>
    </FormItem>
    <FormItem prop="is_assembly">
      <Checkbox v-model="formData.is_assembly">{{ $t('bom_tool.is_assembly') }}?</Checkbox>
    </FormItem>
    <FormItem :label="$t('bom_tool.version_number')" prop="version" v-if="showVersionField">
      <BomVersionSelectByPart :manufacturerPartId="formData.manufacturer_part_id"
                              :hasDraft="true"
                              :bomId.sync="formData.assembly_bom_id"
                              v-model="formData.version"
                              :disabled="!formData.manufacturer_part_id">
      </BomVersionSelectByPart>
    </FormItem>
    <FormItem :label="$t('bom_tool.item_name')" prop="name" :rules="{ required: true, message: $t('bom_tool.item_name_is_required') }" required>
      <Input v-model="formData.name"></Input>
    </FormItem>
    <FormItem :label="$t('bom_tool.item_name_cn')" prop="chinese_name" :rules="{ required: true, message: $t('bom_tool.item_name_cn_is_required') }" required>
      <Input v-model="formData.chinese_name"></Input>
    </FormItem>
    <FormItem :label="$t('bom_tool.specification')" prop="specification" :rules="{ required: true, message: $t('bom_tool.specification_is_required') }" required>
      <Input v-model="formData.specification"></Input>
    </FormItem>
    <Row :gutter='32'>
      <Col span="12">
        <FormItem :label="$t('quantity')" prop="line_quantity" :rules="{ required: true, message: $t('bom_tool.line_quantity_is_required') }" required>
          <InputNumber v-model="formData.line_quantity"></InputNumber>
        </FormItem>
      </Col>
      <Col span="12">
        <FormItem :label="$t('bom_tool.unit')" prop="unit_name" :rules="{ required: true, message: $t('bom_tool.unit_name_is_required') }" required>
          <Select v-model="formData.unit_name">
            <Option v-for="unit in units" :value="unit.value" :key="unit.value">{{ $t(unit.label) }}</Option>
          </Select>
        </FormItem>
      </Col>
    </Row>
    <FormItem :label="$t('bom_tool.designator')" prop="designator">
      <Input type="textarea" v-model="formData.designator"></Input>
    </FormItem>
    <FormItem :label="$t('bom_tool.note')" prop="note">
      <Input type="textarea" v-model="formData.note"></Input>
    </FormItem>
    <FormItem class="text-align-right">
      <Button type="primary" @click="saveBomItem" :loading="saving">{{ $t('save') }}</Button>
    </FormItem>
  </Form>
</template>

<script>
import { mapActions } from 'vuex'
import SelectCompany from "@/view/companies/components/selectCompany"
import ManufacturerPartPicker from '@/view/part/components/manufacturer-part-picker'
import BomVersionSelectByPart from '@/view/bom/versions/bom-versions-select-by-part'
export default {
  name: 'BomItemForm',
  components: {
    ManufacturerPartPicker,
    BomVersionSelectByPart,
    SelectCompany
  },
  props: {
    bomId: Number,
    bomItemData: Object,
    readonly: {
      type: Boolean,
      default: false
    },
    isNew: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    showVersionField () {
      return this.formData.is_assembly && this.formData.manufacturer_part_id
    },
    preventMalwareChange () {
      // Since the plugin data is associated with bom item, we need to prevent the user from changing the associated bom part
      return !this.isNew && this.formData.manufacturer_part_id && this.formData.brand && this.formData.mpn !== ''
    },
    itemPartType () {
      return this.formData.primary_manufacturer_part?.component_type
    }
  },
  data () {
    return {
      formData: {
        primary_manufacturer_part: {}
      },
      saving: false,
      units: [
        { value: 'pcs', label: "part.info.units.pcs" },
        { value: 'batch', label: "part.info.units.batch" },
        { value: 'unit', label: "part.info.units.unit" },
        { value: 'set', label: "part.info.units.set" },
        { value: 'suit', label: "part.info.units.suit" },
        { value: 'roll', label: "part.info.units.roll" },
        { value: 'kg', label: "part.info.units.kg" }
      ]
    }
  },
  mounted () {
    this.formData = _.cloneDeep(this.bomItemData)
  },
  methods: {
    ...mapActions([
      'getPart',
      'updateBomItem',
      'createBomItem'
    ]),
    saveBomItem () {
      this.$refs.bomItemForm.validate((valid) => {
        if (valid) {
          this.saving = true
          if (this.formData.id) {
            // Update
            this.updateBomItem(this.formData).then(resp => {
              this.$emit('bom-item-saved', resp.data)
            }).finally(() => {
              this.saving = false
            })
          } else {
            // Create
            this.formData.bom_id = this.bomId
            this.createBomItem(this.formData).then(resp => {
              this.$emit('bom-item-saved', resp.data)
            }).finally(() => {
              this.saving = false
            })
          }
        }
      })
    },
    populatePropertiesFromManufacturerPart () {
      this.formData.is_assembly = this.formData.primary_manufacturer_part.component_type === 'assembled_part'
      this.formData.name = this.formData.primary_manufacturer_part.name
      this.formData.chinese_name = this.formData.primary_manufacturer_part.chinese_name
      this.formData.specification = this.formData.primary_manufacturer_part.specification
      this.formData.brand = this.formData.primary_manufacturer_part.brand
      this.formData.mpn = this.formData.primary_manufacturer_part.mpn
      this.formData.unit_name = this.formData.primary_manufacturer_part.unit
    }
  },
  watch: {
    isNew: function (val) {
      if (val) {
        this.$refs['bomItemForm'].resetFields()
        this.formData = { primary_manufacturer_part: {} }
      }
    },
    bomItemData: {
      deep: true,
      handler () {
        this.$refs['bomItemForm'].resetFields()
        if (Object.keys(this.bomItemData).length > 0) {
          this.formData = _.cloneDeep(this.bomItemData)
        }
      }
    },
    'formData.manufacturer_part_id': function (value) {
      if (value && this.formData.primary_manufacturer_part) {
        this.populatePropertiesFromManufacturerPart()
      }
    }
  }
}
</script>
